import { parseDate } from "@/libraries/dayjs-relative";
import { transformKeysToSnakeCase } from "@/libraries/transformers";
import { getCalendarDetailQueryOptions } from "@/queries/options/calendar";
import type { EventItem as Event, Reminder } from "@/types";
import { useSuspenseQuery } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import { useMemo } from "react";

export interface ReminderItem extends Reminder {
	unix: number;
	type: "reminder";
}

export interface EventItem extends Event {
	unix: number;
	type: "event";
}

export type CalendarItem = ReminderItem | EventItem;

export interface DateGroupedItem {
	date: Dayjs;
	items: CalendarItem[];
}

interface UseCalendarItemsProps {
	days: Dayjs[];
	startDate: string;
	endDate: string;
}

function useCalendarItems({ days, startDate, endDate }: UseCalendarItemsProps) {
	const payload = useMemo(
		() =>
			transformKeysToSnakeCase({
				startDate,
				endDate,
			}),
		[startDate, endDate],
	);
	const calendarQuery = useSuspenseQuery(
		getCalendarDetailQueryOptions(payload),
	);

	const items = useMemo(() => {
		const calendarItems = [
			...(calendarQuery.data?.reminders?.map((r) => ({
				...r,
				date: parseDate(r.alarm ?? r.created_at).format("DD-MM-YYYY"),
				unix: parseDate(r.alarm ?? r.created_at).unix(),
				type: "reminder" as const,
			})) ?? []),
			...(calendarQuery.data?.events?.map((e) => ({
				...e,
				date: parseDate(e.start_datetime).format("DD-MM-YYYY"),
				unix: parseDate(e.start_datetime).unix(),
				type: "event" as const,
			})) ?? []),
		];

		const grouped = groupBy(orderBy(calendarItems, "unix"), "date");

		return days.map((day) => ({
			date: day,
			items: grouped[day.format("DD-MM-YYYY")] ?? [],
		}));
	}, [calendarQuery.data, days]);

	return { items, ...calendarQuery.data };
}

export default useCalendarItems;
